import React, { useState, useEffect, useCallback, useMemo } from "react";
import { useI18next } from "gatsby-plugin-react-i18next";
import Store from "state/store";
import { useSiteMetadata } from "hooks";
import useLocation from "hooks/use-location";
import {
  findTenant,
  authAdmin,
  refreshAuth,
  logoutAdmin,
  changePassword,
} from "services/auth-service";
import {
  listAdminMfaAsset,
  deleteAdminMfaAsset,
  totpFirstGenerate,
  totpGenerate,
  totpRegister,
  totpVerify,
  emailOtpInitiate,
  emailOtpVerify,
} from "services/admin-mfa-asset-service";
import {
  listApplication as listFubonCreditApplication,
  getApplicationDetail as getFubonCreditApplicationDetail,
  cancelApplication,
  getApplicationDocument,
} from "services/fubon-credit-application-service";
import {
  listApplication,
  exportApplication,
  getApplicationDetail,
  listApplicationJournal,
  createApplicationReview,
  patchApplicationDetail,
  createApplication,
  submitApplication,
  assignApplicationAgent,
  listAccessibleAgent,
} from "services/application-service";
import {
  listApplicationForm,
  getApplicationFormDetail,
  createApplicationForm,
  putApplicationFormMetadata,
  publishApplicationForm,
  deprecateApplicationForm,
  deleteApplicationForm,
  putApplicationFormFieldSchema,
  putApplicationFormPortalDisplaySchema,
  listApplicationFormExample,
  deleteApplicationFormExample,
  putApplicationFormExample,
  createApplicationFormExample,
  patchApplicationFormFieldSchema,
  putApplicationFormGroup,
} from "services/application-form-service";
import {
  listProduct,
  postProduct,
  getProductDetail,
  putProductDetail,
  deleteProductDetail,
} from "services/product-service";
import {
  putUserPersonalInfo,
  putUserPromotionNotification,
  putUserDetail,
  putUserDevice,
  putUserIdentityInfo,
  getUserDetail,
  deleteUserDetail,
  listUser,
} from "services/client-service";
import {
  getAdminDetail,
  postAdmin,
  putAdminDetail,
  deleteAdminDetail,
  putAdminStatus,
  listAdmin,
  listAdminRole,
} from "services/admin-service";
import {
  listAdminGroup,
  postAdminGroup,
  getAdminGroupDetail,
  putAdminGroupDetail,
  deleteAdminGroupDetail,
} from "services/admin-group-service";
import {
  listDeviceRegistration,
  getDeviceRegistrationDetail,
  listBindingAuthHistory,
  getBindingAuthHistoryDetail,
} from "services/device-binding-service";
import {
  listEkycHistory,
  getEkycDetail,
  getEkycImages,
  getEkycRawResult,
  putEkycReviewStatus,
  generateEkycReport,
  synchronizeProviderStatus,
  getServiceSummary,
  getRequestSummary,
} from "services/ekyc-service";
import {
  createEkycCase,
  listEkycCase,
  deleteEkycCaseRecord,
  updateEkycCaseStatus,
  getEkycCaseDetail,
  EkycCaseService,
  listEkycCaseMember,
  updateMemberPersonalInfo,
  updateMemberPhoneNumber,
  deleteMemberPhoneNumber,
  createMemberPhoneNumber,
  listEkycCaseMemberPhoneNumbers,
  listEkycCaseMemberAddresses,
  listEkycCaseMemberIdentities,
  updateMemberAddress,
  deleteMemberAddress,
  createMemberAddress,
  updateMemberIdentity,
  deleteMemberIdentity,
  createMemberIdentity,
  updateMemberEmailAddress,
  deleteMemberEmailAddress,
  createMemberEmailAddress,
  listEkycCaseMemberEmailAddresses,
  getEkycCaseMemberDetail,
  createEkycCaseMember,
  putEkycCase,
} from "services/ekyc-case-service";
import {
  listEkycCaseMemberTransaction,
  initializeEkycTransaction,
  getEkycCaseMemberTransactionDetail,
  EkycCaseTransactionService,
  initializeIdvCheckTransaction,
  listCaseTransactionRequests,
  getCaseTransactionRequestData,
  getCaseRequestDetail,
  getCaseRequestRawResult,
  getCaseRequestImages,
  generateCaseRequestReport,
  putCaseRequestReviewStatus,
  deleteEkycCaseMemberTransaction,
  cancelEkycCaseMemberTransaction,
  getCaseTransactionUrl,
} from "services/ekyc-case-transaction-service";
import {
  getTenantConfiguration,
  putTenantConfiguration,
  putTenantConfigurationStatus,
  performDryrun,
} from "services/configuration-service";
import {
  getTenantInformation,
  putTenantInformation,
} from "services/tenant-information-service";
import {
  listTenantedServiceHealth,
  deleteTenantedServiceHealthRecord,
  deregisterTenantedService,
  purgeOutdatedTenantedServiceHealthRecord,
} from "services/tenanted-service-health-service";
import {
  listMessagingHistory,
  getMessagingDetail,
} from "services/messaging-history-service";
import {
  listMessagingTemplate,
  postMessagingTemplate,
  getMessagingTemplateDetail,
  putMessagingTemplateDetail,
  deleteMessagingTemplateDetail,
  putLocalizedTemplateMessage,
  deleteLocalizedTemplateMessage,
} from "services/messaging-template-service";
import {
  postMessagingSms,
  postMessagingTemplateSms,
  postMessagingBroadcastSms,
  postMessagingBroadcastTemplateSms,
  postMessagingEmail,
  postMessagingTemplateEmail,
  postMessagingBroadcastEmail,
  postMessagingBroadcastTemplateEmail,
  postMessagingPns,
  postMessagingTemplatePns,
  postMessagingBroadcastPns,
  postMessagingBroadcastTemplatePns,
} from "services/new-message-service";
import {
  listExecutionHistory,
  getExecutionHistoryDetail,
} from "services/job-execution-service";
import {
  listTask,
  getTaskDetail,
  putTaskDetail,
  listJob,
  postJob,
  getJobDetail,
  putJobDetail,
  deleteJobDetail,
} from "services/task-service";
import {
  listSystemResource,
  getSystemResourceDetail,
  putSystemResourceDetail,
  postSystemResourceDetail,
  deleteSystemResourceDetail,
} from "services/system-resources-service";
import { performDecrypt, performEncrypt } from "services/encryption-service";
import { setAuth, setT, selectAuthState, clearAuth } from "state/authSlice";
import { useSelector, useDispatch } from "react-redux";
import Loading from "components/loading";
import {
  saveState,
  loadState,
  StandardObject,
  saveLocalState,
  loadLocalState,
} from "utils";

import type {
  CashifyFunctionWrapper,
  WrappedCashifyFunction,
} from "services/utils";
import type {
  TenantService,
  AdminAuthenticationService,
  AuthenticationResponse,
} from "services/auth-service";
import type { AdminService } from "services/admin-service";
import type { AdminGroupService } from "services/admin-group-service";
import type { AdminMfaAssetService } from "services/admin-mfa-asset-service";
import type { ApplicationService as FubonCreditApplicationService } from "services/fubon-credit-application-service";
import type { ApplicationService } from "services/application-service";
import type { ApplicationFormService } from "services/application-form-service";
import type { ClientService } from "services/client-service";
import type { DeviceBindingService } from "services/device-binding-service";
import type { EkycService } from "services/ekyc-service";
import type { MessagingHistoryService } from "services/messaging-history-service";
import type { MessagingTemplateService } from "services/messaging-template-service";
import type { NewMessageService } from "services/new-message-service";
import type { ProductService } from "services/product-service";
import type { SystemResourceService } from "services/system-resources-service";
import type { TenantConfigurationService } from "services/configuration-service";
import type { TenantInformationService } from "services/tenant-information-service";
import type { TenantedServiceHealthService } from "services/tenanted-service-health-service";
import type { TaskService } from "services/task-service";
import type { JobExecutionService } from "services/job-execution-service";
import type { EncryptionService } from "services/encryption-service";
import {
  supportedLanguages,
  type SupportedLocale,
} from "components/supported-language";
import { useAppData } from "./app-data";
import {
  ApplicationGroupService,
  createApplicationGroup,
  deleteApplicationGroupDetail,
  getApplicationGroupDetail,
  listApplicationGroup,
  listSelfGroup,
  putApplicationGroupDetail,
} from "services/application-group-service";

interface TaskJobExecutionService extends TaskService, JobExecutionService {}
interface MessagingService
  extends MessagingHistoryService,
    MessagingTemplateService {}

export interface GatewayService {
  tenant: TenantService;
  adminAuth: AdminAuthenticationService;
  adminMfaAsset: AdminMfaAssetService;
  admin: AdminService;
  adminGroup: AdminGroupService;
  fubonCreditApplication: FubonCreditApplicationService;
  applicationGroup: ApplicationGroupService;
  applicationForm: ApplicationFormService;
  application: ApplicationService;
  binding: DeviceBindingService;
  client: ClientService;
  ekyc: EkycService;
  ekycCase: EkycCaseService;
  ekycCaseTransaction: EkycCaseTransactionService;
  messaging: MessagingService;
  newMessage: NewMessageService;
  product: ProductService;
  resources: SystemResourceService;
  tenantConfiguration: TenantConfigurationService;
  tenantInformation: TenantInformationService;
  tenantedServiceHealth: TenantedServiceHealthService;
  task: TaskJobExecutionService;
  encryption: EncryptionService;
}

export interface GatewayServiceContextType {
  gatewayService: GatewayService;
  handleAuthData: (data: AuthenticationResponse) => void;
  checkRequiredScopesFulfilled: (requiredScopes: string[] | string) => boolean;
}

export const GatewayServiceContext =
  React.createContext<GatewayServiceContextType>(
    {} as GatewayServiceContextType
  );

// https://stackoverflow.com/questions/60257671/wrap-fetch-function-for-token-when-using-reactn
// https://github.com/vlki/refresh-fetch
const getFetchWrapper =
  (
    urlPrefix: string,
    authDataHandler: (data: AuthenticationResponse) => void,
    language: SupportedLocale = "en-US"
  ): CashifyFunctionWrapper =>
  <T extends StandardObject | void, R>(getFunc: WrappedCashifyFunction<T, R>) =>
  async (args: T) => {
    // Get current auth state using Store.getState()
    return Store.dispatch(setT({ funcName: getFunc.name })).then(async () => {
      const state = Store.getState();
      const { tenantId, refreshRequired, atok, username } =
        selectAuthState(state);
      if (tenantId === null || username === null || atok === null) {
        console.error("Failed to renewal access token.");
        Store.dispatch(clearAuth());
        return Promise.reject();
      }
      const options = {
        headers: {
          "x-language": language,
          "x-tenant-id": String(tenantId),
          Authorization: `Bearer ${atok}`,
        },
      };
      const func = getFunc({ urlPrefix, options });
      if (refreshRequired) {
        // atok expired
        // Try renew atok and then call original fetch
        return refreshAuth({ urlPrefix })({ tenantId, username })
          .then((resp) => {
            if (resp.status && resp.status === 200) {
              return resp.json();
            } else {
              throw resp.statusText;
            }
          })
          .then((json) => {
            authDataHandler(json.data);
          })
          .then(() => {
            return func(args);
          })
          .catch((err) => {
            // Renew Access Token failed.
            console.error("Failed to renewal access token. ", err);
            Store.dispatch(clearAuth());
            return Promise.reject();
          });
      } else {
        // atok not expired or not expiring yet. Renewal not required.
        return func(args);
      }
    });
  };

const LOGIN_PATH = "/login";
const MFA_PATH = "/mfa";
const HOME_PATH = "/t/home";
const languages = supportedLanguages.map((lang) => lang.language);

const checkIsLoginPath = (pathname: string): boolean => {
  // match language prefix /en /zh
  // remove trailing slash if exists
  const formattedPathname = pathname.endsWith("/")
    ? pathname.slice(0, -1)
    : pathname;
  return (
    languages.some((lang) => {
      return formattedPathname === `/${lang}${LOGIN_PATH}`;
    }) || formattedPathname === LOGIN_PATH
  );
};

const checkisMfaPath = (pathname: string): boolean => {
  // match language prefix /en /zh
  const formattedPathname = pathname.endsWith("/")
    ? pathname.slice(0, -1)
    : pathname;
  return (
    languages.some((lang) => {
      return formattedPathname === `/${lang}${MFA_PATH}`;
    }) || formattedPathname === MFA_PATH
  );
};

const getLocaleFromLang = (lang: string): SupportedLocale => {
  switch (lang) {
    case "en":
      return "en-US";
    case "zh":
      return "zh-HK";
    case "ja":
      return "ja-JP";
    case "sv":
      return "sv-SE";
    default:
      return "en-US";
  }
};

export const GatewayServiceProvider: React.FC<React.PropsWithChildren> = ({
  children,
}) => {
  const { navigate, language } = useI18next();
  const { URLPREFIX: urlPrefix } = useSiteMetadata();
  const { refresh: refreshAppData } = useAppData();
  const loc = useLocation();
  const dispatch = useDispatch();
  const { scope, isAuthenticated, mfaAuthenticated } =
    useSelector(selectAuthState);
  const [view, setView] = useState(<Loading />);

  const handleAuthData = useCallback(
    (data: AuthenticationResponse) => {
      // Set refresh token as cookie
      // let exp = data.refreshExpiresIn;
      // document.cookie = `hasRefreshToken=true; Secure; Path=/; Max-Age=${exp}; SameSite=Strict`;
      // Store shoft-lived atok in redux store

      dispatch(setAuth(data));
      // Force Refresh App Data
      refreshAppData();
    },
    [dispatch]
  );
  const checkRequiredScopesFulfilled = (
    requiredScopes: string[] | string
  ): boolean => {
    const restricted =
      requiredScopes instanceof Array
        ? requiredScopes.reduce((ret, c) => ret || !scope.includes(c), false)
        : !scope.includes(requiredScopes);
    return !restricted;
  };
  const gatewayService = useMemo(() => {
    const wrapfetch = getFetchWrapper(
      urlPrefix,
      handleAuthData,
      getLocaleFromLang(language)
    );
    return {
      tenant: {
        findTenant: findTenant({
          urlPrefix,
          language: getLocaleFromLang(language),
        }),
      },
      adminAuth: {
        authAdminStandard: authAdmin({
          urlPrefix,
          type: "STANDARD",
          language: getLocaleFromLang(language),
        }),
        authAdminEmergency: authAdmin({
          urlPrefix,
          type: "EMERGENCY",
          language: getLocaleFromLang(language),
        }),
        logoutAdmin: wrapfetch(logoutAdmin),
        changePassword: wrapfetch(changePassword),
      },
      adminMfaAsset: {
        listAdminMfaAsset: wrapfetch(listAdminMfaAsset),
        deleteAdminMfaAsset: wrapfetch(deleteAdminMfaAsset),
        totpFirstGenerate: wrapfetch(totpFirstGenerate),
        totpGenerate: wrapfetch(totpGenerate),
        totpRegister: wrapfetch(totpRegister),
        totpVerify: wrapfetch(totpVerify),
        emailOtpInitiate: wrapfetch(emailOtpInitiate),
        emailOtpVerify: wrapfetch(emailOtpVerify),
      },
      admin: {
        listAdmin: wrapfetch(listAdmin),
        postAdmin: wrapfetch(postAdmin),
        getAdminDetail: wrapfetch(getAdminDetail),
        putAdminDetail: wrapfetch(putAdminDetail),
        deleteAdminDetail: wrapfetch(deleteAdminDetail),
        putAdminStatus: wrapfetch(putAdminStatus),
        listAdminRole: wrapfetch(listAdminRole),
      },
      adminGroup: {
        listAdminGroup: wrapfetch(listAdminGroup),
        postAdminGroup: wrapfetch(postAdminGroup),
        getAdminGroupDetail: wrapfetch(getAdminGroupDetail),
        putAdminGroupDetail: wrapfetch(putAdminGroupDetail),
        deleteAdminGroupDetail: wrapfetch(deleteAdminGroupDetail),
      },
      binding: {
        listDeviceRegistration: wrapfetch(listDeviceRegistration),
        getDeviceRegistrationDetail: wrapfetch(getDeviceRegistrationDetail),
        listBindingAuthHistory: wrapfetch(listBindingAuthHistory),
        getBindingAuthHistoryDetail: wrapfetch(getBindingAuthHistoryDetail),
      },
      ekyc: {
        listEkycHistory: wrapfetch(listEkycHistory),
        getEkycDetail: wrapfetch(getEkycDetail),
        getEkycImages: wrapfetch(getEkycImages),
        getEkycRawResult: wrapfetch(getEkycRawResult),
        putEkycReviewStatus: wrapfetch(putEkycReviewStatus),
        generateEkycReport: wrapfetch(generateEkycReport),
        synchronizeProviderStatus: wrapfetch(synchronizeProviderStatus),
        getServiceSummary: wrapfetch(getServiceSummary),
        getRequestSummary: wrapfetch(getRequestSummary),
      },
      ekycCase: {
        createCase: wrapfetch(createEkycCase),
        listCase: wrapfetch(listEkycCase),
        putCase: wrapfetch(putEkycCase),
        updateCaseStatus: wrapfetch(updateEkycCaseStatus),
        deleteCaseRecord: wrapfetch(deleteEkycCaseRecord),
        getCaseDetail: wrapfetch(getEkycCaseDetail),
        getCaseMemberDetail: wrapfetch(getEkycCaseMemberDetail),
        listCaseMember: wrapfetch(listEkycCaseMember),
        listCaseMemberPhoneNumbers: wrapfetch(listEkycCaseMemberPhoneNumbers),
        listCaseMemberAddresses: wrapfetch(listEkycCaseMemberAddresses),
        listCaseMemberEmailAddresses: wrapfetch(
          listEkycCaseMemberEmailAddresses
        ),
        listCaseMemberIdentities: wrapfetch(listEkycCaseMemberIdentities),
        createCaseMember: wrapfetch(createEkycCaseMember),
        updateMemberPersonalInfo: wrapfetch(updateMemberPersonalInfo),
        updateMemberPhoneNumber: wrapfetch(updateMemberPhoneNumber),
        deleteMemberPhoneNumber: wrapfetch(deleteMemberPhoneNumber),
        createMemberPhoneNumber: wrapfetch(createMemberPhoneNumber),
        updateMemberAddress: wrapfetch(updateMemberAddress),
        deleteMemberAddress: wrapfetch(deleteMemberAddress),
        createMemberAddress: wrapfetch(createMemberAddress),
        updateMemberEmailAddress: wrapfetch(updateMemberEmailAddress),
        deleteMemberEmailAddress: wrapfetch(deleteMemberEmailAddress),
        createMemberEmailAddress: wrapfetch(createMemberEmailAddress),
        updateMemberIdentity: wrapfetch(updateMemberIdentity),
        deleteMemberIdentity: wrapfetch(deleteMemberIdentity),
        createMemberIdentity: wrapfetch(createMemberIdentity),
      },
      ekycCaseTransaction: {
        listCaseMemberTransaction: wrapfetch(listEkycCaseMemberTransaction),
        listTransactionRequests: wrapfetch(listCaseTransactionRequests),
        initializeIdvCheckTransaction: wrapfetch(initializeIdvCheckTransaction),
        initializeEkycTransaction: wrapfetch(initializeEkycTransaction),
        getCaseMemberTransactionDetail: wrapfetch(
          getEkycCaseMemberTransactionDetail
        ),
        deleteCaseMemberTransaction: wrapfetch(deleteEkycCaseMemberTransaction),
        cancelCaseMemberTransaction: wrapfetch(cancelEkycCaseMemberTransaction),
        getCaseTransactionRequestData: wrapfetch(getCaseTransactionRequestData),
        getCaseTransactionUrl: wrapfetch(getCaseTransactionUrl),
        getCaseRequestDetail: wrapfetch(getCaseRequestDetail),
        getCaseRequestRawResult: wrapfetch(getCaseRequestRawResult),
        getCaseRequestImages: wrapfetch(getCaseRequestImages),
        generateCaseRequestReport: wrapfetch(generateCaseRequestReport),
        putCaseRequestReviewStatus: wrapfetch(putCaseRequestReviewStatus),
      },
      messaging: {
        listMessagingHistory: wrapfetch(listMessagingHistory),
        getMessagingDetail: wrapfetch(getMessagingDetail),
        listMessagingTemplate: wrapfetch(listMessagingTemplate),
        postMessagingTemplate: wrapfetch(postMessagingTemplate),
        getMessagingTemplateDetail: wrapfetch(getMessagingTemplateDetail),
        putMessagingTemplateDetail: wrapfetch(putMessagingTemplateDetail),
        deleteMessagingTemplateDetail: wrapfetch(deleteMessagingTemplateDetail),
        putLocalizedTemplateMessage: wrapfetch(putLocalizedTemplateMessage),
        deleteLocalizedTemplateMessage: wrapfetch(
          deleteLocalizedTemplateMessage
        ),
      },
      newMessage: {
        postMessagingSms: wrapfetch(postMessagingSms),
        postMessagingTemplateSms: wrapfetch(postMessagingTemplateSms),
        postMessagingBroadcastSms: wrapfetch(postMessagingBroadcastSms),
        postMessagingBroadcastTemplateSms: wrapfetch(
          postMessagingBroadcastTemplateSms
        ),
        postMessagingEmail: wrapfetch(postMessagingEmail),
        postMessagingTemplateEmail: wrapfetch(postMessagingTemplateEmail),
        postMessagingBroadcastEmail: wrapfetch(postMessagingBroadcastEmail),
        postMessagingBroadcastTemplateEmail: wrapfetch(
          postMessagingBroadcastTemplateEmail
        ),
        postMessagingPns: wrapfetch(postMessagingPns),
        postMessagingTemplatePns: wrapfetch(postMessagingTemplatePns),
        postMessagingBroadcastPns: wrapfetch(postMessagingBroadcastPns),
        postMessagingBroadcastTemplatePns: wrapfetch(
          postMessagingBroadcastTemplatePns
        ),
      },
      tenantConfiguration: {
        getTenantConfiguration: wrapfetch(getTenantConfiguration),
        putTenantConfiguration: wrapfetch(putTenantConfiguration),
        putTenantConfigurationStatus: wrapfetch(putTenantConfigurationStatus),
        dryrun: wrapfetch(performDryrun),
      },
      tenantInformation: {
        getTenantInformation: wrapfetch(getTenantInformation),
        putTenantInformation: wrapfetch(putTenantInformation),
      },
      tenantedServiceHealth: {
        listTenantedServiceHealth: wrapfetch(listTenantedServiceHealth),
        deleteTenantedServiceHealthRecord: wrapfetch(
          deleteTenantedServiceHealthRecord
        ),
        deregisterTenantedService: wrapfetch(deregisterTenantedService),
        purgeOutdatedTenantedServiceHealthRecord: wrapfetch(
          purgeOutdatedTenantedServiceHealthRecord
        ),
      },
      task: {
        listExecutionHistory: wrapfetch(listExecutionHistory),
        getExecutionHistoryDetail: wrapfetch(getExecutionHistoryDetail),
        listTask: wrapfetch(listTask),
        getTaskDetail: wrapfetch(getTaskDetail),
        putTaskDetail: wrapfetch(putTaskDetail),
        listJob: wrapfetch(listJob),
        postJob: wrapfetch(postJob),
        getJobDetail: wrapfetch(getJobDetail),
        putJobDetail: wrapfetch(putJobDetail),
        deleteJobDetail: wrapfetch(deleteJobDetail),
      },
      encryption: {
        decrypt: wrapfetch(performDecrypt),
        encrypt: wrapfetch(performEncrypt),
      },
      client: {
        putUserPersonalInfo: wrapfetch(putUserPersonalInfo),
        putUserPromotionNotification: wrapfetch(putUserPromotionNotification),
        putUserDetail: wrapfetch(putUserDetail),
        putUserDevice: wrapfetch(putUserDevice),
        putUserIdentityInfo: wrapfetch(putUserIdentityInfo),
        getUserDetail: wrapfetch(getUserDetail),
        deleteUserDetail: wrapfetch(deleteUserDetail),
        listUser: wrapfetch(listUser),
      },
      fubonCreditApplication: {
        listApplication: wrapfetch(listFubonCreditApplication),
        getApplicationDetail: wrapfetch(getFubonCreditApplicationDetail),
        cancelApplication: wrapfetch(cancelApplication),
        getApplicationDocument: wrapfetch(getApplicationDocument),
      },
      application: {
        listApplication: wrapfetch(listApplication),
        createApplication: wrapfetch(createApplication),
        submitApplication: wrapfetch(submitApplication),
        exportApplication: wrapfetch(exportApplication),
        getApplicationDetail: wrapfetch(getApplicationDetail),
        patchApplicationDetail: wrapfetch(patchApplicationDetail),
        listApplicationJournal: wrapfetch(listApplicationJournal),
        createApplicationReview: wrapfetch(createApplicationReview),
        assignApplicationAgent: wrapfetch(assignApplicationAgent),
        listAccessibleAgent: wrapfetch(listAccessibleAgent),
      },
      applicationForm: {
        listApplicationForm: wrapfetch(listApplicationForm),
        getApplicationFormDetail: wrapfetch(getApplicationFormDetail),
        createApplicationForm: wrapfetch(createApplicationForm),
        deleteApplicationForm: wrapfetch(deleteApplicationForm),
        putApplicationFormMetadata: wrapfetch(putApplicationFormMetadata),
        publishApplicationForm: wrapfetch(publishApplicationForm),
        deprecateApplicationForm: wrapfetch(deprecateApplicationForm),
        putApplicationFormFieldSchema: wrapfetch(putApplicationFormFieldSchema),
        patchApplicationFormFieldSchema: wrapfetch(
          patchApplicationFormFieldSchema
        ),
        putApplicationFormPortalDisplaySchema: wrapfetch(
          putApplicationFormPortalDisplaySchema
        ),
        putApplicationFormGroup: wrapfetch(putApplicationFormGroup),
        listApplicationFormExample: wrapfetch(listApplicationFormExample),
        deleteApplicationFormExample: wrapfetch(deleteApplicationFormExample),
        putApplicationFormExample: wrapfetch(putApplicationFormExample),
        createApplicationFormExample: wrapfetch(createApplicationFormExample),
      },
      applicationGroup: {
        listGroup: wrapfetch(listApplicationGroup),
        createGroup: wrapfetch(createApplicationGroup),
        getGroupDetail: wrapfetch(getApplicationGroupDetail),
        putGroupDetail: wrapfetch(putApplicationGroupDetail),
        deleteGroupDetail: wrapfetch(deleteApplicationGroupDetail),
        listSelfGroup: wrapfetch(listSelfGroup),
      },
      product: {
        listProduct: wrapfetch(listProduct),
        postProduct: wrapfetch(postProduct),
        getProductDetail: wrapfetch(getProductDetail),
        putProductDetail: wrapfetch(putProductDetail),
        deleteProductDetail: wrapfetch(deleteProductDetail),
      },
      resources: {
        listSystemResource: wrapfetch(listSystemResource),
        getSystemResourceDetail: wrapfetch(getSystemResourceDetail),
        putSystemResourceDetail: wrapfetch(putSystemResourceDetail),
        postSystemResourceDetail: wrapfetch(postSystemResourceDetail),
        deleteSystemResourceDetail: wrapfetch(deleteSystemResourceDetail),
      },
    };
  }, [urlPrefix, language, handleAuthData]);

  useEffect(() => {
    // Save last pathname that is not login
    const isLogin = checkIsLoginPath(loc.pathname);
    if (!isLogin && loc.pathname !== "/change-password") {
      saveState("lastLoc", {
        pathname: loc.pathname,
        search: loc.search,
        hash: loc.hash,
      });
    }
  }, [loc]);

  useEffect(() => {
    // Subscribe to store to save auth to sessionStorage
    const actionList = ["auth/setAuth", "auth/updateUserDetail"];
    const unsubscribe = Store.subscribe(() => {
      let lastAction = Store.getState().history.lastAction;
      if (!!lastAction && actionList.includes(lastAction)) {
        saveState("auth", Store.getState().auth);
        const { tenantId, username } = Store.getState().auth;
        // Persist tenantId, username for refreshAuth
        saveLocalState("auth", { tenantId, username });
      }
    });
    // On first load, try refresh token with tenantId and username in local storage
    const isLogin = checkIsLoginPath(loc.pathname);
    if (!isAuthenticated && !!loc.pathname && !isLogin) {
      // Try refresh auth
      const { tenantId, username } = loadLocalState("auth", [
        "tenantId",
        "username",
      ]);
      if (!!tenantId && !!username) {
        refreshAuth({ urlPrefix })({ tenantId, username })
          .then((resp) => {
            if (resp.status && resp.status === 200) {
              return resp.json();
            } else {
              throw resp.statusText;
            }
          })
          .then((resp: { data: AuthenticationResponse }) => {
            dispatch(setAuth(resp.data));
          })
          .catch((error) => {
            console.error("Error refreshing auth:", error);
            // Optionally handle error, e.g., redirect to login
            navigate(LOGIN_PATH);
          });
      } else {
        navigate(LOGIN_PATH);
      }
    }
    return () => {
      unsubscribe();
    };
  }, []);

  useEffect(() => {
    setView(<Loading />);
    // if (isAuthenticated && passwordExpired && loc.pathname !== "/change-password") {
    //   navigate("/change-password");
    // }
    const canView = isAuthenticated && mfaAuthenticated;
    const isLoginPath = checkIsLoginPath(loc.pathname);
    const isMfaPath = checkisMfaPath(loc.pathname);
    if (canView && (isLoginPath || isMfaPath)) {
      let lastLoc = loadState("lastLoc", ["pathname", "search", "hash"]);
      let lastPathname =
        !!lastLoc && !!lastLoc.pathname ? lastLoc.pathname : "";
      let lastHash = !!lastLoc && !!lastLoc.hash ? lastLoc.hash : "";
      let lastSearch = !!lastLoc && !!lastLoc.search ? lastLoc.search : "";
      let lastUrl = !!lastLoc ? lastPathname + lastSearch + lastHash : "";
      if (lastUrl && !(isLoginPath || isMfaPath)) {
        navigate(lastUrl);
      } else {
        navigate(HOME_PATH);
      }
      return;
    }
    if (isAuthenticated && !mfaAuthenticated && !isMfaPath) {
      navigate(MFA_PATH);
      return;
    }
    if (!isAuthenticated && !isLoginPath) {
      navigate(LOGIN_PATH);
      return;
    }
    if (
      canView ||
      (!isAuthenticated && isLoginPath) ||
      (isAuthenticated && !mfaAuthenticated && isMfaPath)
    ) {
      setView(<>{children}</>);
    }
  }, [loc, isAuthenticated, mfaAuthenticated, children]);

  return (
    <GatewayServiceContext.Provider
      value={{ gatewayService, handleAuthData, checkRequiredScopesFulfilled }}
    >
      {view}
    </GatewayServiceContext.Provider>
  );
};
